import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Target,
  TrendingUp,
  Users,
  CheckCircle2,
  XCircle,
  Handshake
} from "lucide-react";

const BRIDGE_IMG = "https://images.unsplash.com/photo-1560964645-5995275a218d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxhcmNoaXRlY3R1cmFsJTIwYnJpZGdlJTIwc3VzcGVuc2lvbiUyMGZvZyUyMGFic3RyYWN0fGVufDB8fHx8MTc3MzE2NTY0OXww&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } }
};

const criteria = [
  {
    icon: Target,
    title: "Strategic Alignment",
    description: "Businesses whose goals and values align with our long-term vision for creating sustainable value.",
    details: [
      "Clear mission and purpose",
      "Commitment to operational excellence",
      "Openness to strategic change"
    ]
  },
  {
    icon: TrendingUp,
    title: "Growth Potential",
    description: "Companies that demonstrate genuine potential for significant growth and market expansion.",
    details: [
      "Scalable business model",
      "Addressable market opportunity",
      "Competitive differentiation"
    ]
  },
  {
    icon: Users,
    title: "Leadership Commitment",
    description: "Management teams that value structured leadership, accountability, and collaborative partnership.",
    details: [
      "Willingness to embrace guidance",
      "Commitment to transparency",
      "Drive for continuous improvement"
    ]
  }
];

const goodFit = [
  "Companies seeking operational restructuring",
  "Businesses preparing for scale or turnaround",
  "Entrepreneurs launching significant ventures",
  "Organizations needing strategic leadership",
  "Firms looking for capital and expertise combined"
];

const notFit = [
  "Businesses seeking only short-term fixes",
  "Companies resistant to structural change",
  "Organizations looking for passive investment only",
  "Ventures without committed leadership",
  "Businesses without clear growth potential"
];

export default function PartnershipsPage() {
  return (
    <div data-testid="partnerships-page">
      {/* Hero Section */}
      <section className="pt-32 pb-24 md:pt-40 md:pb-32 px-6 md:px-12 lg:px-24 bg-navy-950">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-4xl"
        >
          <motion.p 
            className="text-teal-500 text-xs uppercase tracking-[0.3em] font-semibold mb-4"
            variants={fadeUp}
          >
            Strategic Partnerships
          </motion.p>
          <motion.h1 
            className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-8"
            variants={fadeUp}
          >
            Selective by Design
          </motion.h1>
          <motion.p 
            className="text-slate-400 text-lg md:text-xl leading-relaxed max-w-2xl"
            variants={fadeUp}
          >
            Venture Winds does not work with every company. We partner selectively 
            with businesses that share our commitment to excellence and long-term 
            value creation.
          </motion.p>
        </motion.div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              className="font-playfair text-3xl md:text-4xl font-semibold text-navy-950 mb-6"
              variants={fadeUp}
            >
              Why We're Selective
            </motion.h2>
            <motion.p 
              className="text-slate-500 text-lg leading-relaxed mb-6"
              variants={fadeUp}
            >
              Our hands-on approach to partnership requires deep commitment of time, 
              expertise, and often capital. To deliver meaningful results, we limit 
              the number of businesses we work with at any given time.
            </motion.p>
            <motion.p 
              className="text-slate-500 text-lg leading-relaxed mb-6"
              variants={fadeUp}
            >
              This selectivity ensures that every partnership receives the focused 
              attention it deserves. We're not interested in surface-level engagements 
              or quick transactions—we seek genuine transformational opportunities.
            </motion.p>
            <motion.p 
              className="text-slate-500 text-lg leading-relaxed"
              variants={fadeUp}
            >
              When we commit to a partnership, we commit fully. Our reputation is 
              built on the success of the businesses we work with.
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img
              src={BRIDGE_IMG}
              alt="Bridge emerging from fog"
              className="w-full aspect-[4/3] object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Criteria Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-slate-50" data-testid="criteria-section">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.p 
            className="text-teal-600 text-xs uppercase tracking-[0.3em] font-semibold mb-4"
            variants={fadeUp}
          >
            Partnership Criteria
          </motion.p>
          <motion.h2 
            className="font-playfair text-3xl md:text-4xl font-semibold text-navy-950 mb-6 max-w-2xl"
            variants={fadeUp}
          >
            What We Look For
          </motion.h2>
          <motion.p 
            className="text-slate-500 text-lg leading-relaxed max-w-2xl mb-16"
            variants={fadeUp}
          >
            We partner with businesses that demonstrate these essential qualities.
          </motion.p>

          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            variants={staggerContainer}
          >
            {criteria.map((item, index) => (
              <motion.div
                key={item.title}
                variants={fadeUp}
                className="bg-white p-8 border border-slate-100"
                data-testid={`criteria-card-${index}`}
              >
                <item.icon className="w-10 h-10 text-teal-500 mb-6" strokeWidth={1.5} />
                <h3 className="font-playfair text-xl font-semibold text-navy-950 mb-4">
                  {item.title}
                </h3>
                <p className="text-slate-500 mb-6">
                  {item.description}
                </p>
                <ul className="space-y-2">
                  {item.details.map((detail, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-1 h-1 bg-teal-500 rounded-full" />
                      {detail}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* Fit Assessment */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="fit-section">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.div className="text-center mb-16" variants={fadeUp}>
            <h2 className="font-playfair text-3xl md:text-4xl font-semibold text-navy-950 mb-6">
              Is Venture Winds Right for You?
            </h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">
              Honest assessment of fit is essential for successful partnerships. 
              Here's how to evaluate whether we're aligned.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto"
            variants={staggerContainer}
          >
            {/* Good Fit */}
            <motion.div 
              variants={fadeUp}
              className="bg-slate-50 p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle2 className="w-6 h-6 text-teal-500" strokeWidth={1.5} />
                <h3 className="font-playfair text-xl font-semibold text-navy-950">
                  Good Fit
                </h3>
              </div>
              <ul className="space-y-4">
                {goodFit.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-teal-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 className="w-3 h-3 text-teal-600" strokeWidth={2} />
                    </div>
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Not a Fit */}
            <motion.div 
              variants={fadeUp}
              className="bg-white border border-slate-200 p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <XCircle className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
                <h3 className="font-playfair text-xl font-semibold text-navy-950">
                  Not a Fit
                </h3>
              </div>
              <ul className="space-y-4">
                {notFit.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <XCircle className="w-3 h-3 text-slate-400" strokeWidth={2} />
                    </div>
                    <span className="text-slate-500">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* Partnership Process */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-navy-950">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.p 
            className="text-teal-500 text-xs uppercase tracking-[0.3em] font-semibold mb-4"
            variants={fadeUp}
          >
            The Process
          </motion.p>
          <motion.h2 
            className="font-playfair text-3xl md:text-4xl font-semibold text-white mb-16"
            variants={fadeUp}
          >
            How Partnerships Begin
          </motion.h2>

          <motion.div 
            className="grid md:grid-cols-4 gap-8"
            variants={staggerContainer}
          >
            {[
              { step: "01", title: "Initial Inquiry", desc: "Submit your information through our contact form" },
              { step: "02", title: "Preliminary Review", desc: "We assess alignment with our partnership criteria" },
              { step: "03", title: "Discovery Call", desc: "Mutual exploration of fit and potential" },
              { step: "04", title: "Deep Assessment", desc: "Comprehensive evaluation for qualified opportunities" }
            ].map((phase) => (
              <motion.div
                key={phase.step}
                variants={fadeUp}
                className="text-center"
              >
                <span className="text-teal-500 text-4xl font-playfair font-bold mb-4 block">
                  {phase.step}
                </span>
                <h3 className="font-playfair text-lg font-semibold text-white mb-2">
                  {phase.title}
                </h3>
                <p className="text-slate-400 text-sm">
                  {phase.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center max-w-3xl mx-auto"
        >
          <motion.div variants={fadeUp} className="mb-8">
            <Handshake className="w-16 h-16 text-teal-500 mx-auto" strokeWidth={1} />
          </motion.div>
          <motion.h2 
            className="font-playfair text-3xl md:text-4xl font-semibold text-navy-950 mb-6"
            variants={fadeUp}
          >
            Think We Might Be Aligned?
          </motion.h2>
          <motion.p 
            className="text-slate-500 text-lg leading-relaxed mb-10"
            variants={fadeUp}
          >
            We welcome inquiries from businesses that believe they meet our 
            partnership criteria. Let's start a conversation.
          </motion.p>
          <motion.div variants={fadeUp}>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-navy-950 text-white px-10 py-5 text-sm uppercase tracking-widest font-medium btn-shadow hover:bg-teal-600 transition-colors"
              data-testid="partnerships-cta"
            >
              Submit an Inquiry
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}
